// apps/web/hooks/useModalState.ts
import { useState, useEffect, useMemo } from 'react';

import { GetLinksResponse } from '@lessonPlanner/commons';

import { PageKey } from '../enums/pageKey';
import useLocalStorageItem from '../hooks/useLocalStorageItem';

interface UseModalStateProps {
  isNoCreditsError?: boolean;
  hasGenerationError?: boolean;
  isDoneRequesting?: boolean;
  isUserPremium?: boolean;
  open?: boolean;
  pageKey?: PageKey;
}

export function useModalState({
  isNoCreditsError,
  hasGenerationError,
  isDoneRequesting,
  isUserPremium,
  open,
  pageKey,
}: UseModalStateProps) {
  const [nextActivityNumber, setNextActivityNumber] = useState<number>(1);
  const [linksAndReferences, setLinksAndReferences] = useState<
    GetLinksResponse[]
  >([]);
  const [isMoreActivitiesClicked, setIsMoreActivitiesClicked] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showNoCreditsModal, setShowNoCreditsModal] =
    useState(isNoCreditsError);
  const [topics, setTopics] = useState<string[]>([]);
  const [isFetchingReferences, setIsFetchingReferences] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [prevHasGenerationError, setPrevHasGenerationError] =
    useState(hasGenerationError);

  const { getItemByKey } = useLocalStorageItem();

  useEffect(() => {
    const topicsArr = getItemByKey('topics') || [];
    const topicsText = topicsArr.map((topic) => topic?.topic?.toLowerCase());
    setTopics(topicsText);
  }, []);

  useEffect(() => {
    setPrevHasGenerationError(hasGenerationError);
  }, [hasGenerationError]);

  useEffect(() => {
    setShowNoCreditsModal(isNoCreditsError);
  }, [isNoCreditsError]);

  const canGenerateLinksAndReferences = useMemo(
    () =>
      isDoneRequesting &&
      !hasGenerationError &&
      !(prevHasGenerationError && !hasGenerationError) &&
      !isNoCreditsError &&
      isUserPremium &&
      open &&
      [PageKey.lessonPlan].includes(pageKey),
    [
      isDoneRequesting,
      hasGenerationError,
      isNoCreditsError,
      isUserPremium,
      open,
      pageKey,
    ],
  );

  return {
    nextActivityNumber,
    setNextActivityNumber,
    linksAndReferences,
    setLinksAndReferences,
    isMoreActivitiesClicked,
    setIsMoreActivitiesClicked,
    showConfirmationModal,
    setShowConfirmationModal,
    showNoCreditsModal,
    setShowNoCreditsModal,
    topics,
    setTopics,
    isFetchingReferences,
    setIsFetchingReferences,
    isDownloading,
    setIsDownloading,
    canGenerateLinksAndReferences,
  };
}
